import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";

const STORAGE_KEY = "cookieConsent.v1";

// Default categories
const defaultPrefs = {
  essential: true, // always on
  analytics: true,
  marketing: true,
};

export const CookieConsentContext = createContext({
  prefs: defaultPrefs,
  visible: false,
  details: false,
  openPreferences: (_details = true) => {},
  closePreferences: () => {},
  acceptAll: () => {},
  rejectNonEssential: () => {},
  save: (_p) => {},
});

export function CookieConsentProvider({ children }) {
  const [prefs, setPrefs] = useState(defaultPrefs);
  const [visible, setVisible] = useState(false);
  const [details, setDetails] = useState(false);
  const hasMounted = useRef(false);

  // Load from storage once
  useEffect(() => {
    if (hasMounted.current) return;
    hasMounted.current = true;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.prefs) {
          setPrefs({ ...defaultPrefs, ...parsed.prefs, essential: true });
          setVisible(false);
          return;
        }
      }
    } catch (_) {}
    // If nothing stored yet, show banner
    setVisible(true);
    setDetails(false);
  }, []);

  const persist = useCallback((p) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ prefs: { ...p, essential: true }, ts: Date.now() })
      );
      // Also set a simple cookie for the backend to read (for server-side CAPI gating)
      const parts = [
        `a=${p.analytics ? 1 : 0}`,
        `m=${p.marketing ? 1 : 0}`,
      ];
      const maxAge = 60 * 60 * 24 * 180; // 180 days
      const secure = (typeof window !== 'undefined' && window.location && window.location.protocol === 'https:') ? '; Secure' : '';
      document.cookie = `cm_consent=${parts.join('.')}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
    } catch (_) {}
  }, []);

  const openPreferences = useCallback((openDetails = true) => { setVisible(true); setDetails(!!openDetails); }, []);
  const closePreferences = useCallback(() => { setVisible(false); setDetails(false); }, []);

  const acceptAll = useCallback(() => {
    const next = { ...defaultPrefs, analytics: true, marketing: true };
    setPrefs(next);
    persist(next);
    setVisible(false);
    setDetails(false);
  }, [persist]);

  const rejectNonEssential = useCallback(() => {
    const next = { ...defaultPrefs, analytics: false, marketing: false };
    setPrefs(next);
    persist(next);
    setVisible(false);
    setDetails(false);
  }, [persist]);

  const save = useCallback((p) => {
    const next = { ...defaultPrefs, ...p, essential: true };
    setPrefs(next);
    persist(next);
    setVisible(false);
    setDetails(false);
  }, [persist]);

  const value = useMemo(
    () => ({ prefs, visible, details, openPreferences, closePreferences, acceptAll, rejectNonEssential, save }),
    [prefs, visible, details, openPreferences, closePreferences, acceptAll, rejectNonEssential, save]
  );

  return (
    <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>
  );
}
